import React, { useState, useCallback } from 'react';
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';
import Card from './Card';
import Button from './Button';
import { fetchSemrushData } from '../api';
import { useToast } from '../ToastContext';
import {
    TrendingUp, Key, Shield, RefreshCw, AlertCircle, ExternalLink, Info, BarChart3, Globe
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
    if (n === null || n === undefined) return null;
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'k';
    return String(n);
}

// ── Metric definitions used for tooltips ─────────────────────────────────────

const DEFS = {
    organic_traffic:
        'Estimation du nombre de visiteurs mensuels provenant des résultats naturels (non payants) de Google. Null si le site n\'a pas assez de trafic pour être suivi par Semrush.',
    organic_keywords:
        'Nombre de mots-clés pour lesquels le site apparaît dans le top 100 des résultats Google (base France). Null pour les sites très récents ou peu référencés.',
    organic_cost:
        'Estimation du coût mensuel pour acquérir ce même volume de trafic organique via Google Ads (en dollars).',
    paid_traffic:
        'Estimation du nombre de visiteurs mensuels provenant d\'annonces payantes (Google Ads).',
    paid_keywords:
        'Nombre de mots-clés pour lesquels le site achète des annonces Google Ads.',
    semrush_rank:
        'Le classement global du site dans la base Semrush. Plus le chiffre est petit, plus le site a de la visibilité globale.'
};

// ── InfoTooltip (matches ScoreCard pattern) ──────────────────────────────────

function InfoTip({ text }) {
    return (
        <div className="relative group/tip inline-flex ml-1">
            <Info size={13} className="text-blue-gray-400 group-hover/tip:text-blue-gray-600 transition-colors cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 w-64 p-3 bg-blue-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 pointer-events-none z-20 leading-relaxed">
                {text}
                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-blue-gray-900" />
            </div>
        </div>
    );
}

// ── MetricTile (follows app DA: bg-neutral-50, border-neutral-200/50, accent icon) ──

function MetricTile({ icon: Icon, label, value, unit, definition, iconColor }) {
    const hasValue = value !== null && value !== undefined;
    const color = iconColor || 'text-accent';
    return (
        <div className="rounded-xl border border-blue-gray-100 bg-white p-4 flex flex-col h-full shadow-sm">
            <div className="flex items-start justify-between mb-2">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Icon className={color} size={16} />
                </div>
                {definition && <InfoTip text={definition} />}
            </div>
            <div className="text-2xl font-bold text-blue-gray-900 mb-0.5 tabular-nums">
                {hasValue ? fmt(value) : <span className="text-blue-gray-400 text-sm font-normal">—</span>}
                {hasValue && unit && <span className="text-xs text-blue-gray-500 font-medium ml-1">{unit}</span>}
            </div>
            <div className="text-[11px] font-medium text-blue-gray-500 uppercase tracking-wide mt-auto pt-1 leading-snug">{label}</div>
        </div>
    );
}

// ── MarcheMetricCards (exporté pour usage dans TabScoreHeader, version compacte) ─

export function MarcheMetricCards({ semrushData }) {
    if (!semrushData) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <MarcheMetricCard compact icon={TrendingUp} label="Visites / mois" value={semrushData.organic_traffic} unit="/mois" definition={DEFS.organic_traffic} />
            <MarcheMetricCard compact icon={Key} label="Mots-clés" value={semrushData.organic_keywords} definition={DEFS.organic_keywords} />
        </div>
    );
}

function MarcheMetricCard({ icon: Icon, label, value, unit, definition, compact }) {
    const hasValue = value !== null && value !== undefined;
    const displayVal = hasValue ? (value >= 1000 ? (value / 1000).toFixed(1) + 'k' : String(value)) : null;
    if (compact) {
        return (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-blue-gray-100 min-h-[52px] shadow-sm">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <Icon className="text-accent" size={14} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-base font-bold text-blue-gray-900 leading-tight tabular-nums">
                        {hasValue ? displayVal : '—'}
                        {hasValue && unit && <span className="text-xs text-blue-gray-500 font-normal ml-0.5">{unit}</span>}
                    </div>
                    <div className="text-xs text-blue-gray-500 font-medium">{label}</div>
                </div>
                {definition && (
                    <div className="relative group shrink-0">
                        <Info size={12} className="text-blue-gray-400" />
                        <div className="absolute bottom-full right-0 mb-1 w-48 p-2 bg-blue-gray-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            {definition}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function SemrushOverview({ semrushData, fetchedAt, slug, onRefresh, noKey, hideHeader, noCard, minimal }) {
    const { showToast } = useToast();
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await fetchSemrushData(slug);
            showToast('Données Semrush mises à jour !', 'success');
            if (onRefresh) onRefresh();
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Erreur lors de la mise à jour Semrush';
            showToast(msg, 'error');
        } finally {
            setRefreshing(false);
        }
    }, [slug, onRefresh, showToast]);

    const Wrapper = noCard ? 'div' : Card;

    // ── Empty states ──────────────────────────────────────────────────────────

    if (noKey) {
        return (
            <Wrapper>
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                        <AlertCircle className="text-amber-500" size={16} />
                    </div>
                    <div>
                        <p className="font-heading font-semibold text-blue-gray-800 text-sm mb-1">Données Semrush non disponibles</p>
                        <p className="text-sm text-blue-gray-500 leading-relaxed">
                            Configurez votre clé API Semrush dans{' '}
                            <a href="/profile" className="text-accent underline inline-flex items-center gap-1">
                                Profil &gt; Clés API <ExternalLink size={12} />
                            </a>{' '}
                            pour afficher le trafic et les mots-clés de ce projet.
                        </p>
                    </div>
                </div>
            </Wrapper>
        );
    }

    if (!semrushData) {
        return (
            <Wrapper>
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                        <AlertCircle className="text-accent" size={16} />
                    </div>
                    <div>
                        <p className="font-heading font-semibold text-blue-gray-800 text-sm mb-1">Données Semrush en attente</p>
                        <p className="text-sm text-blue-gray-500 mb-3 leading-relaxed">
                            Lancez un audit complet ou cliquez ci-dessous pour récupérer les données.
                        </p>
                        {slug && (
                            <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
                                <RefreshCw size={14} className={refreshing ? 'animate-spin mr-1.5' : 'mr-1.5'} />
                                {refreshing ? 'Récupération...' : 'Récupérer maintenant'}
                            </Button>
                        )}
                    </div>
                </div>
            </Wrapper>
        );
    }

    if (semrushData.error && semrushData.organic_traffic === null && semrushData.organic_keywords === null) {
        return (
            <Wrapper>
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                        <AlertCircle className="text-red-500" size={16} />
                    </div>
                    <div>
                        <p className="font-heading font-semibold text-blue-gray-800 text-sm mb-1">Erreur Semrush</p>
                        <p className="text-sm text-blue-gray-500">{semrushData.error}</p>
                        {slug && (
                            <Button variant="secondary" size="sm" className="mt-3" onClick={handleRefresh} disabled={refreshing}>
                                <RefreshCw size={14} className={refreshing ? 'animate-spin mr-1.5' : 'mr-1.5'} />
                                Réessayer
                            </Button>
                        )}
                    </div>
                </div>
            </Wrapper>
        );
    }

    const fetchedLabel = fetchedAt
        ? new Date(fetchedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : null;

    const orgTraffic = semrushData.organic_traffic || 0;
    const paidTraffic = semrushData.paid_traffic || 0;
    const totalTraffic = orgTraffic + paidTraffic;
    const pctOrg = totalTraffic > 0 ? (orgTraffic / totalTraffic) * 100 : 50;
    const pctPaid = totalTraffic > 0 ? (paidTraffic / totalTraffic) * 100 : 50;

    const trafficPieData = [
        { name: 'SEO', value: totalTraffic > 0 ? orgTraffic : 1, color: '#10b981' },
        { name: 'Ads', value: totalTraffic > 0 ? paidTraffic : 1, color: '#3b82f6' }
    ];

    // Mode minimal (audit gratuit) : les cartes sont affichées via MarcheMetricCards dans TabScoreHeader
    if (minimal) {
        return null;
    }

    return (
        <Wrapper>
            {/* En-tête aligné sur BlockHeader (Synthèse) */}
            {!hideHeader && (
                <div className="flex items-center justify-between gap-2 mb-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-accent/10 flex-shrink-0">
                            <BarChart3 size={15} className="text-accent" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-heading font-semibold text-blue-gray-800 text-base leading-tight">Données de marché</h2>
                            <p className="text-xs text-blue-gray-500 mt-0.5 truncate">
                                <span className="text-blue-gray-600 font-medium">Semrush</span>
                                {fetchedLabel && <> · Mis à jour le {fetchedLabel}</>}
                                {semrushData.database && <> · Base {(semrushData.database).toUpperCase()}</>}
                            </p>
                        </div>
                    </div>
                    {slug && (
                        <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing} className="shrink-0">
                            <RefreshCw size={14} className={refreshing ? 'animate-spin mr-1.5' : 'mr-1.5'} />
                            {refreshing ? 'Actualisation…' : 'Actualiser'}
                        </Button>
                    )}
                </div>
            )}

            {/* Organique */}
            <div className="text-xs font-medium text-blue-gray-500 mb-3">Performance organique</div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <MetricTile icon={TrendingUp} label="Trafic organique" value={semrushData.organic_traffic} unit="/mois" definition={DEFS.organic_traffic} />
                    <MetricTile icon={Key} label="Mots-clés organiques" value={semrushData.organic_keywords} definition={DEFS.organic_keywords} />
                    <MetricTile icon={Shield} label="Valeur du trafic (équivalent Ads)" value={semrushData.organic_cost} unit="$ /mois" definition={DEFS.organic_cost} />
                    <MetricTile icon={Globe} label="Classement Semrush (monde)" value={semrushData.semrush_rank} definition={DEFS.semrush_rank} />
                </div>

                <div className="rounded-xl border border-blue-gray-100 bg-white p-4 flex flex-col h-full justify-between shadow-sm">
                    <div>
                        <div className="flex items-start justify-between mb-3">
                            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                                <Key size={16} className="text-accent" />
                            </div>
                            <InfoTip text="Meilleur mot-clé organique apportant le plus de trafic au site. Affiche la position et la part du trafic total généré." />
                        </div>
                        <div className="text-[10px] font-medium text-blue-gray-400 uppercase tracking-wide mb-1">Top mot-clé Google</div>
                        <div className="text-lg font-bold text-blue-gray-900 truncate" title={semrushData.top_keyword || ''}>
                            {semrushData.top_keyword || <span className="text-blue-gray-400 font-normal text-sm">—</span>}
                        </div>
                    </div>

                    {semrushData.top_keyword && (
                        <div className="mt-4">
                            <div className="flex justify-between text-[11px] text-blue-gray-500 mb-1.5 font-medium">
                                <span>Part du trafic</span>
                                <span className="text-accent font-bold tabular-nums">{semrushData.top_keyword_traffic}%</span>
                            </div>
                            <div className="w-full bg-blue-gray-100 rounded-full h-1.5">
                                <div className="bg-accent h-1.5 rounded-full transition-[width]" style={{ width: `${Math.min(100, semrushData.top_keyword_traffic || 0)}%` }} />
                            </div>
                            <div className="mt-3 text-[11px] text-blue-gray-600 bg-blue-gray-50 border border-blue-gray-100 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-2">
                                <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                                Position Google <span className="font-semibold text-blue-gray-800">#{semrushData.top_keyword_position}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="border-t border-blue-gray-100 pt-5">
                <div className="text-xs font-medium text-blue-gray-500 mb-3">Trafic payant & répartition</div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <div className="rounded-xl border border-blue-gray-100 bg-white p-4 flex flex-col justify-center items-center shadow-sm">
                        <div className="text-[11px] font-medium text-blue-gray-500 mb-3 text-center uppercase tracking-wide">SEO vs Ads</div>
                        {totalTraffic > 0 ? (
                            <>
                                <div className="relative w-32 h-32">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={trafficPieData}
                                                dataKey="value"
                                                innerRadius={36}
                                                outerRadius={52}
                                                stroke="none"
                                                paddingAngle={1}
                                            >
                                                {trafficPieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(val, name) => [`${fmt(val)}/mois`, name]}
                                                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, padding: '8px 12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                        <span className="text-lg font-bold text-blue-gray-900 tabular-nums">{Math.round(pctOrg)}%</span>
                                        <span className="text-[10px] text-blue-gray-500 font-medium">SEO</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center gap-5 mt-3 text-[11px] font-medium text-blue-gray-600">
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" /> SEO {Math.round(pctOrg)}%
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0" /> Ads {Math.round(pctPaid)}%
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-blue-gray-400 text-sm text-center py-6">Aucun trafic détecté</div>
                        )}
                    </div>

                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <MetricTile icon={TrendingUp} label="Visiteurs Ads estimés" value={semrushData.paid_traffic} unit="/mois" definition={DEFS.paid_traffic} iconColor="text-blue-500" />
                        <MetricTile icon={Key} label="Mots-clés Ads achetés" value={semrushData.paid_keywords} definition={DEFS.paid_keywords} iconColor="text-blue-500" />
                    </div>
                </div>
            </div>
        </Wrapper>
    );
}
